import { PauseCircleFilled, PlayCircleFilled, SoundOutlined } from '@ant-design/icons'
import { Segmented, Slider, Tooltip, Typography, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import B2BLayout from '../../../layouts/b2b'
import { playSoundPreset, preloadSoundFiles, SoundPlayback } from './audio-player'
import {
  SOUND_CATEGORY_META,
  SOUND_CATEGORY_ORDER,
  SOUND_PRESETS,
  SoundCategory,
  SoundPreset,
  getSoundDuration,
} from './presets'

const { Text, Title } = Typography
const PRIMARY_COLOR = 'var(--system-button, var(--system-primary, #61688a))'
type CategoryFilter = 'all' | SoundCategory

const breadcrumbs = [
  { href: '/admin', title: 'Админка' },
  { href: '/admin/notification-sounds', title: 'Звуки уведомлений' },
]

const appear = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`

const pulse = keyframes`
  0%, 100% { transform: scaleY(0.45); opacity: 0.55; }
  50% { transform: scaleY(1); opacity: 1; }
`

const Workspace = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  animation: ${appear} 180ms ease-out;
`

const Controls = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 20px;
  align-items: end;
  padding: 2px 0 16px;
  background: #fff;
  border-bottom: 1px solid #e9ebef;

  @media (max-width: 760px) {
    position: static;
    grid-template-columns: 1fr;
    gap: 14px;
  }
`

const ControlMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const HeadingRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0;
  }
`

const Filter = styled(Segmented)`
  width: fit-content;
  max-width: 100%;

  @media (max-width: 620px) {
    width: 100%;

    .ant-segmented-group {
      overflow-x: auto;
    }

    .ant-segmented-item-label {
      padding-inline: 10px;
      white-space: nowrap;
    }
  }
`

const VolumeControl = styled.div`
  display: grid;
  grid-template-columns: 18px 150px 38px;
  gap: 10px;
  align-items: center;
  min-width: 216px;
  padding-bottom: 3px;

  .ant-slider {
    margin: 8px 0;
  }

  @media (max-width: 760px) {
    grid-template-columns: 18px minmax(120px, 1fr) 38px;
    min-width: 0;
  }
`

const Library = styled.div`
  display: flex;
  flex-direction: column;
`

const CategorySection = styled.section`
  padding: 20px 0 4px;
  border-bottom: 1px solid #e9ebef;

  &:last-child {
    border-bottom: 0;
  }
`

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 9px;
`

const CategoryMarker = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${({ $color }) => $color};
`

const CategoryMeta = styled.div`
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
`

const SoundGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 28px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`

const SoundRow = styled.div<{ $active: boolean }>`
  min-width: 0;
  display: grid;
  grid-template-columns: 34px minmax(120px, 1fr) 86px 44px;
  gap: 10px;
  align-items: center;
  min-height: 58px;
  padding: 7px 8px 7px 2px;
  border-bottom: 1px solid #f0f1f3;
  transition: background-color 140ms ease, padding-left 140ms ease;

  &:hover {
    padding-left: 6px;
    background: #f7f8fa;
  }

  ${({ $active }) =>
    $active &&
    css`
      padding-left: 6px;
      background: color-mix(in srgb, ${PRIMARY_COLOR} 8%, #ffffff);
    `}

  @media (max-width: 520px) {
    grid-template-columns: 34px minmax(0, 1fr) 62px 38px;
    gap: 8px;
  }
`

const PlayButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${({ $active }) => ($active ? PRIMARY_COLOR : '#69727d')};
  cursor: pointer;
  transition: color 140ms ease, transform 140ms ease, background-color 140ms ease;

  &:hover {
    color: ${PRIMARY_COLOR};
    background: #eef0f4;
    transform: scale(1.06);
  }

  &:focus-visible {
    outline: 2px solid ${PRIMARY_COLOR};
    outline-offset: 2px;
  }

  svg {
    width: 22px;
    height: 22px;
  }
`

const SoundInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SoundName = styled(Text)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Waveform = styled.div`
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
`

const WaveBar = styled.span<{ $height: number; $active: boolean; $delay: number }>`
  display: block;
  width: 3px;
  height: ${({ $height }) => $height}px;
  border-radius: 2px;
  background: ${({ $active }) => ($active ? PRIMARY_COLOR : '#c8cdd4')};
  transform-origin: center;
  transition: background-color 140ms ease;
  ${({ $active, $delay }) =>
    $active &&
    css`
      animation: ${pulse} 420ms ease-in-out ${$delay}ms infinite;
    `}
`

const Duration = styled(Text)`
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
`

const getWaveform = (preset: SoundPreset): number[] => {
  if ('audioUrl' in preset) {
    const seed = Array.from(preset.id).reduce((sum, character) => sum + character.charCodeAt(0), 0)
    return Array.from({ length: 10 }, (_, index) => 5 + ((seed + index * 7) % 15))
  }

  return Array.from({ length: 10 }, (_, index) => {
    const tone = preset.tones[index % preset.tones.length]
    const frequencyFactor = Math.min(tone.frequency / 60, 12)
    return Math.round(5 + ((frequencyFactor + index * 2.3 + preset.tones.length) % 15))
  })
}

const formatDuration = (preset: SoundPreset): string => `${getSoundDuration(preset).toFixed(1)} с`

const NotificationSoundsPage = () => {
  const [category, setCategory] = useState<CategoryFilter>('all')
  const [volume, setVolume] = useState(72)
  const [activeSoundId, setActiveSoundId] = useState<string | null>(null)
  const playbackRef = useRef<SoundPlayback | null>(null)
  const finishTimerRef = useRef<number | null>(null)

  const visibleCategories = useMemo(
    () => (category === 'all' ? SOUND_CATEGORY_ORDER : [category]),
    [category],
  )
  const visibleCount = useMemo(
    () => SOUND_PRESETS.filter(preset => visibleCategories.includes(preset.category)).length,
    [visibleCategories],
  )

  const stopPlayback = useCallback(() => {
    playbackRef.current?.stop()
    playbackRef.current = null
    if (finishTimerRef.current) {
      window.clearTimeout(finishTimerRef.current)
      finishTimerRef.current = null
    }
    setActiveSoundId(null)
  }, [])

  const handlePlay = useCallback(
    async (preset: SoundPreset) => {
      if (activeSoundId === preset.id) {
        stopPlayback()
        return
      }

      stopPlayback()
      const playback = await playSoundPreset(preset, volume)
      if (!playback) {
        message.warning('Браузер не поддерживает воспроизведение звука')
        return
      }

      playbackRef.current = playback
      setActiveSoundId(preset.id)
      finishTimerRef.current = window.setTimeout(() => {
        playbackRef.current = null
        finishTimerRef.current = null
        setActiveSoundId(null)
      }, (playback.duration + 0.12) * 1000)
    },
    [activeSoundId, stopPlayback, volume],
  )

  useEffect(() => stopPlayback, [stopPlayback])

  useEffect(() => {
    preloadSoundFiles(SOUND_PRESETS)
  }, [])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle="Звуки уведомлений">
      <Workspace>
        <Controls>
          <ControlMain>
            <HeadingRow>
              <Title level={3}>Библиотека звуков</Title>
              <Text type="secondary">{visibleCount} вариантов</Text>
            </HeadingRow>
            <Filter
              value={category}
              onChange={value => {
                stopPlayback()
                setCategory(value as CategoryFilter)
              }}
              options={[
                { label: 'Все', value: 'all' },
                ...SOUND_CATEGORY_ORDER.map(key => ({
                  label: SOUND_CATEGORY_META[key].label,
                  value: key,
                })),
              ]}
            />
          </ControlMain>
          <VolumeControl>
            <SoundOutlined />
            <Slider
              min={0}
              max={100}
              value={volume}
              onChange={setVolume}
              tooltip={{ formatter: value => `${value}%` }}
              aria-label="Громкость предпрослушивания"
            />
            <Text type="secondary">{volume}%</Text>
          </VolumeControl>
        </Controls>

        <Library>
          {visibleCategories.map(categoryKey => {
            const meta = SOUND_CATEGORY_META[categoryKey]
            const presets = SOUND_PRESETS.filter(preset => preset.category === categoryKey)
            return (
              <CategorySection key={categoryKey}>
                <CategoryHeader>
                  <CategoryMarker $color={meta.color} />
                  <CategoryMeta>
                    <Text strong>{meta.label}</Text>
                    <Text type="secondary">{meta.description}</Text>
                  </CategoryMeta>
                </CategoryHeader>
                <SoundGrid>
                  {presets.map(preset => {
                    const isActive = activeSoundId === preset.id
                    const waveform = getWaveform(preset)
                    return (
                      <SoundRow key={preset.id} $active={isActive}>
                        <Tooltip title={isActive ? 'Остановить' : 'Прослушать'}>
                          <PlayButton
                            type="button"
                            $active={isActive}
                            onClick={() => handlePlay(preset)}
                            aria-label={`${isActive ? 'Остановить' : 'Прослушать'} ${preset.name}`}
                          >
                            {isActive ? <PauseCircleFilled /> : <PlayCircleFilled />}
                          </PlayButton>
                        </Tooltip>
                        <SoundInfo>
                          <SoundName strong={isActive}>{preset.name}</SoundName>
                          <Text type="secondary" ellipsis>
                            {preset.character}
                          </Text>
                        </SoundInfo>
                        <Waveform aria-hidden="true">
                          {waveform.map((height, index) => (
                            <WaveBar
                              key={`${preset.id}-${index}`}
                              $height={height}
                              $active={isActive}
                              $delay={index * 28}
                            />
                          ))}
                        </Waveform>
                        <Duration type="secondary">{formatDuration(preset)}</Duration>
                      </SoundRow>
                    )
                  })}
                </SoundGrid>
              </CategorySection>
            )
          })}
        </Library>
      </Workspace>
    </B2BLayout>
  )
}

export default NotificationSoundsPage
